import type { IAssistantEditedNotificationData } from "@frontend-shared/types";
import { PencilLine } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";
import { FIELD_LABEL_KEYS } from "./constants";

interface IAssistantEditedCardProps {
  uuid: string;
  data: IAssistantEditedNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewAssistants: (projectUuid: string) => void;
}

const AssistantEditedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewAssistants,
}: IAssistantEditedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const changedFieldsText = useMemo(() => {
    const labels = data.changedFields.map((field) => {
      // NOTE: `field` is a server-supplied string; narrow to the known set so
      // the Record lookup type-checks while still letting unknown keys fall
      // through to the raw value (handled by the `? :` below).
      const key = FIELD_LABEL_KEYS[field as keyof typeof FIELD_LABEL_KEYS];
      return key ? t(key) : field;
    });
    return labels.join(", ");
  }, [data.changedFields, t]);

  const hasFields = data.changedFields.length > 0;

  const handleViewAssistants = useCallback(
    () => onViewAssistants(data.projectUuid),
    [onViewAssistants, data.projectUuid],
  );

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="assistant_edited"
      createdAt={createdAt}
      isRead={isRead}
      icon={<PencilLine className="h-5 w-5 text-indigo-500" />}
      iconBgClassName="bg-indigo-500/10"
      ctaLabel={t("view_assistants")}
      onCtaClick={handleViewAssistants}
      body={
        <Trans
          ns="notifications"
          i18nKey={
            hasFields
              ? "type.assistant_edited.body"
              : "type.assistant_edited.body_no_fields"
          }
          values={{
            editorName: data.editorName,
            assistantName: data.assistantName,
            changedFields: changedFieldsText,
          }}
          components={{ b: <strong /> }}
        />
      }
    />
  );
};

export const AssistantEditedCard = memo(AssistantEditedCardComponent);
